import React from "react";
import { SelectItem, Button } from "@heroui/react";
import AppSelect from "../common/appSelect";
import { ArrowLeftIcon, ArrowRightIcon, BriefcaseIcon } from "@heroicons/react/24/outline";
import { TFunction } from "react-i18next";
import { ValidationErrors } from "../../types/types";
import hoowlrLogo from "../../assets/images/logo.svg";
import loginBanner from "../../assets/images/login-banner.png";

interface ManagerDto {
  type?: string;
}

interface FormState {
  formData: {
    profileType?: "MANAGER" | "FIGHTER" | "ORGANIZATION";
    managerDto?: ManagerDto;
  };
}

interface ManagerFormStageProps {
  t: TFunction<"signUpSetupStages", undefined>;
  state: FormState;
  validationErrors: ValidationErrors;
  handleFormChange: (value: string | any, field: string | any, dto: string | any) => void;
  handleFormSubmit: (e: React.FormEvent | any) => void;
  handleBack: () => void;
  isSubmitting: boolean;
}

const labelClass =
  "!text-slate-600 lg:!text-[#71717A] data-[invalid=true]:!text-[#71717A] group-data-[invalid=true]:!text-[#71717A] text-xs lg:text-sm font-semibold lg:font-medium leading-4 lg:leading-5 text-left self-start pb-0 after:hidden";

const selectClassNames = {
  base: "gap-1",
  label: labelClass,
  trigger:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] data-[focus=true]:border-[#2563EB] data-[open=true]:border-[#2563EB]",
  value:
    "text-xs sm:text-sm leading-5 text-[#18181B] group-data-[has-value=false]:text-slate-400 lg:group-data-[has-value=false]:text-[#71717A]",
  selectorIcon: "text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const tertiaryButtonClass =
  "h-10 rounded-2xl lg:rounded-3xl bg-[#EBEBEC] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-[#18181B] hover:bg-[#dcdcde] active:scale-[0.98] transition-all";

const ManagerFormStage: React.FC<ManagerFormStageProps> = ({
  t,
  state,
  validationErrors,
  handleFormChange,
  handleFormSubmit,
  handleBack,
  isSubmitting,
}) => {
  const isManager = state.formData.profileType === "MANAGER";

  return (
    <div className="flex min-h-dvh w-full bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#18181B]">
      {/* LEFT COLUMN */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="
            flex min-h-dvh flex-col justify-between
            px-6 py-6 sm:px-12 sm:py-8
            lg:py-9
            [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6
            [@media(max-height:700px)]:lg:py-3
          "
        >
          {/* Header: logo */}
          <div className="flex w-full items-center justify-between">
            <img
              src={hoowlrLogo}
              alt="Hoowlr"
              className="
                h-9 w-auto object-contain object-left
                lg:h-16 lg:w-48
                [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14
                [@media(max-height:700px)]:lg:h-10
              "
            />
          </div>

          {/* Centered content */}
          <div className="flex flex-1 flex-col items-center justify-center my-auto w-full py-2">
            <div className="flex w-full flex-col items-center gap-1 sm:gap-2 text-center mb-4 sm:mb-6 lg:mb-8 [@media(max-height:700px)]:mb-2">
              <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold lg:font-extrabold leading-tight lg:leading-10 text-[#18181B]">
                {t("managerForm.title")}
              </h1>
              <p className="text-xs sm:text-sm lg:text-base leading-4 lg:leading-6 text-[#71717A]">
                {t("managerForm.subtitle")}
              </p>
            </div>

            <form
              noValidate
              onSubmit={handleFormSubmit}
              className="
                flex w-full max-w-96 flex-col gap-4 sm:gap-5 lg:gap-6
                [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
              "
            >
              <div
                className="
                  flex flex-col gap-3 lg:gap-4
                  [@media(max-height:700px)]:gap-2.5 lg:[@media(max-height:700px)]:gap-3
                "
              >
                {isManager && (
                  <AppSelect
                    name="type"
                    label={t("managerForm.managerType")}
                    labelPlacement="outside"
                    placeholder={t("managerForm.managerTypePlaceholder")}
                    selectedKeys={
                      state.formData.managerDto?.type
                        ? [state.formData.managerDto.type]
                        : []
                    }
                    onSelectionChange={(keys) => {
                      const selected = Array.from(keys)[0] as string | undefined;
                      handleFormChange(selected || "", "type", "managerDto");
                    }}
                    isRequired
                    isInvalid={!!validationErrors["managerDto.type"]}
                    errorMessage={validationErrors["managerDto.type"]}
                    className="w-full"
                    classNames={selectClassNames}
                    startContent={
                      <BriefcaseIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                    }
                  >
                    <SelectItem key="Individual">Individual</SelectItem>
                    <SelectItem key="Agency">Agency</SelectItem>
                  </AppSelect>
                )}
              </div>

              {/* Footer: Back / Submit */}
              <div className="flex w-full items-center justify-between gap-3 pt-4 sm:pt-6 [@media(max-height:700px)]:pt-3">
                <Button
                  onPress={handleBack}
                  variant="flat"
                  startContent={<ArrowLeftIcon className="h-4 w-4" />}
                  className={tertiaryButtonClass}
                >
                  {t("navigation.back")}
                </Button>

                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  endContent={!isSubmitting && <ArrowRightIcon className="h-4 w-4" />}
                  className="h-10 rounded-2xl lg:rounded-3xl bg-[#0485F7] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-white hover:bg-[#0374d6] active:scale-[0.99] transition-all shadow-sm disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {isSubmitting ? t("navigation.submitting") : t("navigation.submit")}
                </Button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <p className="pt-2 pb-1 text-center text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]">
            {t("footer_copyright", "© Hoowlr 2026, All rights reserved")}
          </p>
        </div>
      </div>

      {/* RIGHT BANNER (hidden below lg) */}
      <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
          <img
            src={loginBanner}
            alt=""
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default ManagerFormStage;
